import React, { useState, useMemo, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Avatar,
  Tooltip,
  Chip,
  Select,
  MenuItem as SelectItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AdminPanelSettings as AdminIcon,
  CloudUpload as CloudUploadIcon,
  ListAlt as ListAltIcon,
  RocketLaunch as RocketLaunchIcon,
  History as HistoryIcon,
  Description as DescriptionIcon,
  Group as GroupIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import revmacLogo from '../assets/images/revmac-logo.svg';
import revmacIcon from '../assets/images/revmac-icon.png';
import { useAuth } from '../contexts/AuthContext';
import { PracticeContext } from '../contexts/PracticeContext';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

interface LayoutProps {
  children: React.ReactNode;
  user: any;
  signOut?: () => void;
}

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
}

const Layout: React.FC<LayoutProps> = ({ children, user, signOut }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const location = useLocation();
  const { isAdmin, role } = useAuth();
  // Optional so Layout still renders outside a PracticeProvider (tests, auth
  // shells) - the selector just doesn't appear.
  const practiceCtx = useContext(PracticeContext);
  const practices = practiceCtx?.practices ?? [];
  // Biller management is authorized for ADMIN and CREDENTIALING_ADMIN on the
  // backend (@PreAuthorize on BillerController), so gate the nav the same way.
  const canManageBillers = isAdmin || role === 'CREDENTIALING_ADMIN';

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleSidebarToggle = () => setSidebarCollapsed(!sidebarCollapsed);

  const currentDrawerWidth = sidebarCollapsed ? collapsedDrawerWidth : drawerWidth;

  const menuItems: MenuItem[] = useMemo(() => (
    [
      { text: 'Claims',    icon: <ListAltIcon />,       path: '/claims' },
      { text: 'Upload',    icon: <CloudUploadIcon />,   path: '/upload' },
      { text: 'Status',    icon: <RocketLaunchIcon />,  path: '/status' },
      { text: 'EOBs',      icon: <DescriptionIcon />,   path: '/eobs' },
      { text: 'Audit log', icon: <HistoryIcon />,       path: '/audit-events' },
      // Practices, Providers and Billers are the admin setup surface (backend gates
      // these to ADMIN/CREDENTIALING_ADMIN): assign billers to practices so they can bill.
      ...(canManageBillers ? [
        { text: 'Practices', icon: <BusinessIcon />, path: '/practices' },
        { text: 'Providers', icon: <PersonIcon />,   path: '/providers' },
        { text: 'Billers',   icon: <GroupIcon />,    path: '/billers' },
      ] : []),
    ]
  ), [canManageBillers]);

  const drawer = (
    <div>
      <Toolbar
        sx={{
          py: 3,
          px: sidebarCollapsed ? 1.5 : 2,
          display: 'flex',
          alignItems: 'center',
          gap: sidebarCollapsed ? 0 : 2,
          justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
          minHeight: '80px !important',
        }}
      >
        {sidebarCollapsed ? (
          <img src={revmacIcon} alt="RevMAC" style={{ height: 32, width: 'auto' }} />
        ) : (
          <>
            <img src={revmacLogo} alt="RevMAC Logo" style={{ height: 28, width: 'auto' }} />
            <Box>
              <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700, color: '#162B51' }}>
                RevMAC
              </Typography>
              <Typography variant="caption" sx={{ color: '#616161' }}>
                Billing
              </Typography>
            </Box>
          </>
        )}
      </Toolbar>

      <Box sx={{ display: 'flex', justifyContent: sidebarCollapsed ? 'center' : 'flex-end', px: 1, pb: 1 }}>
        <IconButton
          onClick={handleSidebarToggle}
          sx={{ color: '#162B51', '&:hover': { backgroundColor: '#f5f5f5' } }}
        >
          {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>

      <Divider />

      <List sx={{ px: sidebarCollapsed ? 0.5 : 1, pt: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <Tooltip
              title={sidebarCollapsed ? item.text : ''}
              placement="right"
              disableHoverListener={!sidebarCollapsed}
            >
              <ListItemButton
                component={Link}
                to={item.path}
                selected={location.pathname === item.path}
                sx={{
                  borderRadius: 2,
                  mx: sidebarCollapsed ? 0.5 : 0,
                  px: sidebarCollapsed ? 1.5 : 2,
                  py: 1.5,
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'initial',
                  '&.Mui-selected': {
                    backgroundColor: '#E3F2FD',
                    color: '#1976d2',
                    '&:hover': { backgroundColor: '#BBDEFB' },
                  },
                  '&:hover': { backgroundColor: '#f5f5f5' },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === item.path ? '#1976d2' : 'inherit',
                    minWidth: sidebarCollapsed ? 'auto' : 40,
                    mr: sidebarCollapsed ? 0 : 1.5,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { sm: `${currentDrawerWidth}px` },
          transition: 'width 0.3s, margin-left 0.3s',
          borderRadius: 0,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
            {menuItems.find((item) =>
              location.pathname === item.path || location.pathname.startsWith(`${item.path}/`),
            )?.text || 'RevMAC Billing'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {practices.length > 1 && (
              <Select
                value={practiceCtx?.currentPracticeId ?? ''}
                onChange={(e) => practiceCtx?.setCurrentPracticeId(e.target.value)}
                size="small"
                variant="outlined"
                data-testid="practice-selector"
                inputProps={{ 'aria-label': 'Practice' }}
                sx={{
                  color: 'white',
                  minWidth: 220,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                  '& .MuiSvgIcon-root': { color: 'white' },
                }}
              >
                {practices.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </Select>
            )}
            {practices.length === 1 && (
              <Typography variant="body2" sx={{ color: 'white', opacity: 0.85 }}>
                {practices[0].name}
              </Typography>
            )}
            {isAdmin && (
              <Chip
                icon={<AdminIcon />}
                label="Admin"
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '& .MuiChip-icon': { color: 'white' },
                }}
              />
            )}
            <Avatar sx={{ bgcolor: isAdmin ? '#ff6b35' : '#2395F1', width: 32, height: 32 }}>
              {(user?.email || user?.username || 'U')[0].toUpperCase()}
            </Avatar>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
              {user?.email || user?.username}
            </Typography>
            <Button
              color="inherit"
              onClick={signOut}
              startIcon={<LogoutIcon />}
              sx={{
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
              }}
            >
              Sign Out
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: currentDrawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              transition: 'width 0.3s',
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 4,
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          maxWidth: 'none',
          transition: 'width 0.3s',
          backgroundColor: '#fafafa',
          minHeight: '100vh',
          overflow: 'hidden',
        }}
      >
        <Toolbar />
        <Box sx={{ mt: 2 }}>{children}</Box>
      </Box>
    </Box>
  );
};

export default Layout;
